'use client';

import React, { useState } from 'react';
import { Wheat, Package, ArrowRightLeft, BarChart2, RotateCcw, TrendingUp, AlertTriangle, Download } from 'lucide-react';
import { FEED_SUMMARY } from './feedData';
import FeedTypesTab from './FeedTypesTab';
import FeedStockTab from './FeedStockTab';
import FeedIssuanceTab from './FeedIssuanceTab';
import FeedConsumptionTab from './FeedConsumptionTab';
import FeedReturnsTab from './FeedReturnsTab';
import FCRAnalyticsTab from './FCRAnalyticsTab';
import { MetricCard } from '@/components/ui/Cards';
import Icon from '@/components/ui/AppIcon';



type TabId = 'types' | 'stock' | 'issuance' | 'consumption' | 'returns' | 'fcr';

const TABS: { id: TabId; label: string; icon: React.ElementType; description: string }[] = [
  { id: 'types', label: 'Feed Types', icon: Wheat, description: 'Manage feed catalog' },
  { id: 'stock', label: 'Feed Stock', icon: Package, description: 'Current inventory levels' },
  { id: 'issuance', label: 'Feed Issuance', icon: ArrowRightLeft, description: 'Issue feed to flocks' },
  { id: 'consumption', label: 'Consumption', icon: BarChart2, description: 'Daily consumption tracking' },
  { id: 'returns', label: 'Returns', icon: RotateCcw, description: 'Feed returns & wastage' },
  { id: 'fcr', label: 'FCR Analytics', icon: TrendingUp, description: 'Feed conversion ratios' },
];

export default function FeedManagementContent() {
  const [activeTab, setActiveTab] = useState<TabId>('stock');

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-2 rounded-xl" style={{ backgroundColor: 'var(--primary-light, #eef2ff)' }}>
              <Wheat size={18} className="text-indigo-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Feed Management</h1>
          </div>
          <p className="text-sm text-gray-500 ml-11">Track feed types, stock, issuance, consumption, returns, and FCR across all flocks</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-600">
          <Download size={14} /> Export Report
        </button>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Feed Types', value: FEED_SUMMARY.totalFeedTypes, sub: `${FEED_SUMMARY.activeFeedTypes} active`, iconColor: '#4f46e5', iconBg: '#eef2ff', icon: Wheat },
          { label: 'Stock Value', value: `${(FEED_SUMMARY.totalStockValueUGX / 1000000).toFixed(1)}M`, sub: 'UGX total', iconColor: '#15803d', iconBg: '#f0fdf4', icon: Package },
          { label: 'Low Stock', value: FEED_SUMMARY.lowStockItems, sub: 'items', iconColor: '#d97706', iconBg: '#fffbeb', icon: AlertTriangle },
          { label: 'Out of Stock', value: FEED_SUMMARY.outOfStockItems, sub: 'items', iconColor: '#dc2626', iconBg: '#fef2f2', icon: AlertTriangle },
          { label: "Today's Issues", value: `${FEED_SUMMARY.todayIssuancesKg}kg`, sub: 'issued today', iconColor: '#2563eb', iconBg: '#eff6ff', icon: ArrowRightLeft },
          { label: 'Total Wastage', value: `${FEED_SUMMARY.totalWastageKg.toFixed(1)}kg`, sub: 'recorded', iconColor: '#c2410c', iconBg: '#fff7ed', icon: BarChart2 },
          { label: 'Avg FCR', value: FEED_SUMMARY.avgFCR, sub: 'all broilers', iconColor: '#7c3aed', iconBg: '#faf5ff', icon: TrendingUp },
          { label: 'Flocks Fed', value: 5, sub: 'active flocks', iconColor: '#0f766e', iconBg: '#f0fdfa', icon: Wheat },
        ].map(card => (
          <MetricCard
            key={card.label}
            label={card.label}
            value={card.value}
            sub={card.sub}
            icon={card.icon}
            iconBg={card.iconBg}
            iconColor={card.iconColor}
          />
        ))}
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto scrollbar-thin pb-px">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === 'types' && <FeedTypesTab />}
        {activeTab === 'stock' && <FeedStockTab />}
        {activeTab === 'issuance' && <FeedIssuanceTab />}
        {activeTab === 'consumption' && <FeedConsumptionTab />}
        {activeTab === 'returns' && <FeedReturnsTab />}
        {activeTab === 'fcr' && <FCRAnalyticsTab />}
      </div>
    </div>
  );
}
